// Briques communes du schéma JRAF : identifiants, valeurs numériques
// (nombre ou expression), couleurs, style, easing.

import { z } from 'zod';
import { JRAF_LIMITS as L } from '../limits';
import { HEX_COLOR_RE, JRAF_COLOR_NAMES } from '../palette';

/** Identifiant d'animation, d'objet, de scène, d'étape ou de ressource. */
export const JRAF_ID_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;
export const jrafIdSchema = z
  .string()
  .regex(JRAF_ID_RE, 'identifiant : minuscules, chiffres et tirets, 40 caractères au plus');

/**
 * Identifiant de paramètre : il apparaît dans les expressions (`a * u`), donc
 * pas de tiret (ce serait une soustraction) et pas de nom de fonction.
 */
export const JRAF_PARAM_ID_RE = /^[a-z][a-z0-9_]{0,19}$/;
/** Noms déjà pris par l'évaluateur d'expressions (`@jeraisonne/db/shared/expr`). */
export const RESERVED_PARAM_IDS = [
  'sin',
  'cos',
  'tan',
  'sqrt',
  'abs',
  'min',
  'max',
  'exp',
  'log',
  'ln',
  'sign',
  'pi',
  'e',
  'math',
] as const;
export const jrafParamIdSchema = z
  .string()
  .regex(JRAF_PARAM_ID_RE, 'paramètre : une lettre puis lettres, chiffres ou _, 20 au plus')
  .refine((id) => !(RESERVED_PARAM_IDS as readonly string[]).includes(id), {
    message: 'ce nom est réservé par les expressions (fonction ou constante)',
  });

/** Identifiant d'un outil de la boîte à outils (catalogue de l'app). */
export const TOOL_ID_RE = /^[a-z][a-z0-9-]{1,40}$/;

/**
 * Valeur numérique : un nombre, ou une expression lue par l'évaluateur sûr
 * (`a * u`, `260 / a`) avec les paramètres du document. Jamais d'`eval`.
 */
export const numExprSchema = z.union([
  z.number().finite().min(-L.maxCoordinate).max(L.maxCoordinate),
  z.string().trim().min(1).max(L.maxExpressionChars),
]);
export type NumExpr = z.infer<typeof numExprSchema>;

/** Nombre AFFICHÉ (`value`, `value.set`) : bornes plus larges qu'une coordonnée. */
export const valueExprSchema = z.union([
  z.number().finite().min(-L.maxValueMagnitude).max(L.maxValueMagnitude),
  z.string().trim().min(1).max(L.maxExpressionChars),
]);

export const colorSchema = z.union([
  z.enum(JRAF_COLOR_NAMES as [string, ...string[]]),
  z.string().regex(HEX_COLOR_RE, 'couleur : un nom de la palette ou un hex #rrggbb'),
]);
export const paintSchema = z.union([z.literal('none'), colorSchema]);

export const pointSchema = z.object({ x: numExprSchema, y: numExprSchema }).strict();

export const styleSchema = z
  .object({
    fill: paintSchema.optional(),
    stroke: paintSchema.optional(),
    strokeWidth: z.number().min(0).max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    dash: z.boolean().optional(),
  })
  .strict();
export type JrafStyle = z.infer<typeof styleSchema>;

/**
 * Cadre d'un texte, d'une formule ou d'un nombre : dessiné par le navigateur
 * AUTOUR du texte réellement rendu, avec une marge garantie — jamais un `rect`
 * posé à la main (l'IA ne connaît pas la largeur d'un texte). Il apparaît,
 * bouge et disparaît avec son texte ; `object.style` change sa bordure
 * (`stroke`, `strokeWidth`).
 */
export const textBoxSchema = z
  .object({
    /** bordure ; défaut : la couleur du texte */
    stroke: paintSchema.optional(),
    /** fond ; défaut : aucun */
    fill: paintSchema.optional(),
    strokeWidth: z.number().min(0).max(6).default(2),
    /** marge entre le texte et la bordure */
    padding: z.number().min(4).max(24).default(10),
    radius: z.number().min(0).max(24).default(10),
  })
  .strict();
export type JrafTextBox = z.infer<typeof textBoxSchema>;

export const EASINGS = ['linear', 'easeIn', 'easeOut', 'easeInOut'] as const;
export const easingSchema = z.enum(EASINGS);
export type JrafEasing = z.infer<typeof easingSchema>;

/**
 * Marque des textes affichés : la validation (`displayTextProblems`) vérifie
 * leurs `$` et le LaTeX de leurs formules. Leurs clés sont dans
 * `DISPLAY_TEXT_KEYS` (test : `display-text.test.ts`).
 */
export const DISPLAY_TEXT = 'jraf:display-text';

/** Texte affiché (`$…$` permis, rendu par `MathText` : jamais de HTML). */
export const displayTextSchema = (max: number) =>
  z.string().trim().min(1).max(max).describe(DISPLAY_TEXT);

/** Texte affiché court dont les espaces comptent (préfixe « x = », suffixe « cm »). */
export const displayAffixSchema = (max: number) => z.string().max(max).describe(DISPLAY_TEXT);
